import styled from '@emotion/styled';
import { Button, InformationTip, TitlePart, Checkbox, Slider, DatePicker } from '@bloomays-lib/ui.shared';
import type { DatePickerValue } from '@bloomays-lib/ui.shared';
import { IMissionCardDetails } from '@bloomays-lib/types.shared';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LabelAndSelectField from './LabelAndSelectField';
import { useMutation } from '@apollo/client';
import { GET_ONE_FULL_MISSION, UPDATE_MISSION } from '@bloomays-lib/adapter.api-bloomer';
import { cancel, notify } from '../../helpers/toastify';
import isEqual from 'lodash/fp/isEqual';
import Stack from '@mui/material/Stack';
import RawFormControlLabel from '@mui/material/FormControlLabel';
import { SelectChangeEvent } from '@mui/material/Select';
import { startOfToday } from 'date-fns/startOfToday';
import { startOfMonth } from 'date-fns/startOfMonth';
import { convertDateToStringFormat, isBefore, parseStringToDate } from '@bloomays-lib/utils.shared';

export type ModifyMissionProps = {
  mission: IMissionCardDetails;
  setPendingMissionModify: Dispatch<SetStateAction<boolean>>;
  handleClose: () => void;
};

export const ModifyMission = ({ mission, setPendingMissionModify, handleClose }: ModifyMissionProps) => {
  const { t } = useTranslation(['mission', 'random', 'notify']);

  const initialBegin = useMemo(() => parseStringToDate(mission.beginMission, 'yyyy-MM-dd'), [mission.beginMission]);
  const initialEnd = useMemo(() => parseStringToDate(mission.endMission, 'yyyy-MM-dd'), [mission.endMission]);

  const [beginMission, setBeginMission] = useState<DatePickerValue>(initialBegin);
  const [endMission, setEndMission] = useState<DatePickerValue>(initialEnd);
  const [certifyNoAmendment, setCertifyNoAmendment] = useState<boolean>(false);

  const isMissionStarted = useMemo(() => {
    if (!initialBegin) return false;
    const todayStart = startOfToday();
    // isBefore is stricter than isAfter, so we need to invert the comparison
    return !isBefore(todayStart, initialBegin);
  }, [initialBegin]);

  const [updateMissionMutation, { loading: savingMission }] = useMutation(UPDATE_MISSION, {
    refetchQueries: [
      {
        query: GET_ONE_FULL_MISSION,
        variables: {
          recordId: mission.recordId,
        },
      },
    ],
  });

  const initialState = useMemo(
    () => ({
      contractType: mission.contractType || '',
      shouldBeCountAsDeal: !!mission.shouldBeCountAsDeal,
      replacement: !!mission.replacement,
      hoursPerDay: mission.hoursPerDays,
      enableCRAUpload: !!mission.enableCRAUpload,
      enableBusinessReferalNpsClient: !!mission.enableBusinessReferalNpsClient,
      enableBusinessReferalNpsBloomer: !!mission.enableBusinessReferalNpsBloomer,
      beginMission: initialBegin,
      endMission: initialEnd,
    }),
    [mission, initialBegin, initialEnd],
  );
  const [contractType, setContractType] = useState<string>(mission.contractType || '');
  const [shouldBeCountAsDeal, setShouldBeCountAsDeal] = useState<boolean>(!!mission.shouldBeCountAsDeal);
  const [replacement, setReplacement] = useState<boolean>(!!mission.replacement);
  const [hoursPerDay, setHoursPerDay] = useState<number>(mission.hoursPerDays);
  const [modified, setModified] = useState<boolean>(false);
  const [enableCRAUpload, setEnableCRAUpload] = useState<boolean>(!!mission.enableCRAUpload);
  const [enableBusinessReferalNpsClient, setEnableBusinessReferalNpsClient] = useState<boolean>(
    !!mission.enableBusinessReferalNpsClient,
  );
  const [enableBusinessReferalNpsBloomer, setEnableBusinessReferalNpsBloomer] = useState<boolean>(
    !!mission.enableBusinessReferalNpsBloomer,
  );

  useEffect(() => {
    setPendingMissionModify(savingMission);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingMission]);

  const currentState = useMemo(
    () => ({
      contractType,
      shouldBeCountAsDeal,
      replacement,
      hoursPerDay,
      enableCRAUpload,
      enableBusinessReferalNpsClient,
      enableBusinessReferalNpsBloomer,
      beginMission,
      endMission,
    }),
    [
      contractType,
      shouldBeCountAsDeal,
      replacement,
      hoursPerDay,
      enableCRAUpload,
      enableBusinessReferalNpsClient,
      enableBusinessReferalNpsBloomer,
      beginMission,
      endMission,
    ],
  );

  useEffect(() => {
    setModified(!isEqual(initialState, currentState));
  }, [currentState, initialState]);

  const handleChangeContract = (value: string) => {
    setContractType(value);
  };

  return (
    <ContainerModifyMission>
      <ContainerTitle>
        <TitlePart textTitle={t('modifyMissionTitle')} />
      </ContainerTitle>
      {!isMissionStarted && (
        <ContainerDate>
          <ContainerCheckbox>
            <Checkbox
              aria-label="modifymission-certifyNoAmendment"
              name="certifyNoAmendment"
              checked={certifyNoAmendment}
              label={t('mission:certifyNoAmendment')}
              labelPlacement="start"
              onChange={(_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                setCertifyNoAmendment(checked);
              }}
            />
          </ContainerCheckbox>
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <span>{t('mission:dateDebut')}</span>
              <DatePicker
                ariaLabel="modifymission-begindate"
                disabled={!certifyNoAmendment}
                value={beginMission}
                minDate={startOfMonth(new Date())}
                maxDate={endMission ?? undefined}
                handleChange={(date: DatePickerValue) => {
                  setBeginMission(date);
                }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <span>{t('mission:dateFin')}</span>
              <DatePicker
                disabled={!certifyNoAmendment}
                value={endMission}
                handleChange={(date: DatePickerValue) => setEndMission(date)}
                minDate={beginMission ?? undefined}
              />
            </Stack>
          </Stack>
        </ContainerDate>
      )}
      <ContainerCheckbox>
        <Checkbox
          name="replacement"
          checked={replacement}
          label={t('isReplacement')}
          labelPlacement="start"
          onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setReplacement(checked);
          }}
        />
      </ContainerCheckbox>
      <ContainerCheckbox>
        <Checkbox
          name="shouldBeCountAsDeal"
          checked={shouldBeCountAsDeal}
          label={t('shouldBeCountAsDeal')}
          labelPlacement="start"
          onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setShouldBeCountAsDeal(checked);
          }}
        />
      </ContainerCheckbox>
      {!mission?.contractType && (
        <ContainerContractType>
          <LabelAndSelectField
            handlechange={(e: SelectChangeEvent<unknown>) => handleChangeContract(e.target.value as string)}
            options={[
              { value: 'devis', label: 'Devis envoyé par Bloomays' },
              { value: 'clientContract', label: 'Contrat de prestation de service rédigé par le Client' },
              { value: 'bloomaysContract', label: 'Contrat de prestation de service envoyé par Bloomays' },
              { value: 'BDC', label: 'Bon de commande' },
            ]}
            textLabel={t('contractTypeTitle')}
            value={contractType}
            defaultOption
            name={`contractType`}
            required={false}
          />
        </ContainerContractType>
      )}
      <ContainerSlider>
        <Stack direction="row" spacing={2} alignItems={'baseline'}>
          <FormControlLabel
            label={"Durée de travail d'une journée"}
            labelPlacement={'start'}
            control={
              <>
                <Slider
                  sx={{ width: '100px', marginLeft: '10px' }}
                  min={0}
                  max={12}
                  step={0.5}
                  onChangeCommitted={(e: React.SyntheticEvent | Event, value: number | number[]) => {
                    setHoursPerDay(Array.isArray(value) ? value?.[0] : value);
                  }}
                  defaultValue={hoursPerDay}
                  label={"Base horairie d'un journée de travail"}
                  marks={[]}
                />
                <InformationTip
                  children={"⚠️ Attention la modification n'est pas retractive. Seuls les nouveaux CRA seront affectés"}
                />
              </>
            }
          />
          <div>{hoursPerDay}</div>
        </Stack>
      </ContainerSlider>
      <ContainerCheckbox>
        <Checkbox
          name="updloadCRA"
          checked={enableCRAUpload}
          label={t('canUploadCra')}
          labelPlacement="start"
          onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setEnableCRAUpload(checked);
          }}
        />
      </ContainerCheckbox>
      <ContainerCheckbox>
        <Checkbox
          name="businessReferalNpsClient"
          checked={enableBusinessReferalNpsClient}
          label={t('businessReferalNpsClient')}
          labelPlacement="start"
          onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setEnableBusinessReferalNpsClient(checked);
          }}
        />
      </ContainerCheckbox>
      <ContainerCheckbox>
        <Checkbox
          name="businessReferalNpsBloomer"
          checked={enableBusinessReferalNpsBloomer}
          label={t('businessReferalNpsBloomer')}
          labelPlacement="start"
          onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setEnableBusinessReferalNpsBloomer(checked);
          }}
        />
      </ContainerCheckbox>
      <ContainerButton>
        <Button
          name="save"
          aria-label="modififymission-save"
          textButton={t('save', { ns: 'random' })}
          disable={!modified}
          onClick={async () => {
            const payload: Record<string, unknown> = {
              shouldBeCountAsDeal: shouldBeCountAsDeal,
              replacement: replacement,
              contractType: contractType ? contractType : undefined,
              hoursPerDays: hoursPerDay,
              enableCRAUpload: enableCRAUpload,
              enableBusinessReferalNpsClient: enableBusinessReferalNpsClient,
              enableBusinessReferalNpsBloomer: enableBusinessReferalNpsBloomer,
            };
            if (!isMissionStarted) {
              if (beginMission != null) {
                payload.beginMission = convertDateToStringFormat(beginMission, 'yyyy-MM-dd');
              }
              if (endMission != null) {
                payload.endMission = convertDateToStringFormat(endMission, 'yyyy-MM-dd');
              }
            }

            try {
              notify('info', t('updateInProgress', { ns: 'notify' }), null, {
                autoClose: 800,
                hideProgressBar: true,
              });
              const response = await updateMissionMutation({
                variables: {
                  input: {
                    recordId: mission.recordId,
                    ...payload,
                  },
                },
              });
              if (response?.data.updateMission) {
                notify('success', t('updateSuccess', { ns: 'notify' }));
                handleClose();
              }
            } catch (e: any) {
              cancel();
              if (e.graphqlErrors?.[0]?.extensions?.code === 'BAD_USER_INPUT') {
                const invalidArgs = e.graphqlErrors?.[0]?.extensions?.invalidArgs as Array<{
                  missing: string;
                }>;
                if (invalidArgs)
                  notify(
                    'error',
                    t('missingFieldError', {
                      ns: 'notify',
                      missingField: invalidArgs[0].missing as string,
                    }),
                  );
              } else {
                notify('error', t('randomUpdateError', { ns: 'notify' }));
              }
            }
          }}
        />
      </ContainerButton>
    </ContainerModifyMission>
  ) as React.ReactNode;
};

const ContainerModifyMission = styled('div')(() => ({
  padding: '50px',
  textAlign: 'left',
}));

const ContainerTitle = styled('div')(() => ({
  paddingBottom: '50px',
}));

const ContainerCheckbox = styled('div')(() => ({
  textAlign: 'left',
}));

const ContainerSlider = styled('div')(() => ({
  textAlign: 'left',
}));

const ContainerButton = styled('div')(() => ({
  textAlign: 'right',
  paddingLeft: '50px',
}));

const ContainerContractType = styled('div')(() => ({
  textAlign: 'left',
}));

const ContainerDate = styled('div')(() => ({
  textAlign: 'left',
  padding: '15px',
  marginBottom: '24px',
  borderRadius: '10px',
  boxShadow: '2px 2px 20px lightgrey',
}));

const FormControlLabel = styled(RawFormControlLabel)(() => ({
  marginLeft: '0px ',
}));
